import React, { useState } from 'react'
import type { MenuProps } from '@material-ui/core/Menu'
import Menu from '@material-ui/core/Menu'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core'
import type { MenuItemProps } from '@material-ui/core/MenuItem'
import classnames from 'classnames'
import type { StyleClasses } from '../../styles'

export interface PopupMenuProps extends Omit<MenuProps, 'open'> {
  id?: string
  className?: string
  disabled?: boolean
  component: React.ReactElement<any>
  children: React.ReactElement<MenuItemProps> | Array<React.ReactElement<MenuItemProps>>
  getContentAnchorEl?: any
  orientation?: 'vertical' | 'horizontal'
  classes?: StyleClasses<typeof useStyles>
}

const useStyles = makeStyles<Theme, PopupMenuProps>(
  (_theme: Theme) => ({
    root: (props) => ({
      display: 'inline-block',
      ...(props.orientation === 'horizontal' && {})
    }),
    menu: (props) => ({
      ...(props.orientation === 'horizontal' && {
        display: 'flex',
        flexDirection: 'row'
      })
    }),
    list: (props) => ({
      ...(props.orientation === 'horizontal' && {
        display: 'flex',
        padding: 0
      })
    }),
    divider: {}
  }),
  { name: 'PopupMenu' }
)

function PopupMenu(props: PopupMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const classes = useStyles(props)

  const handleToggle = (event: React.SyntheticEvent) => {
    const target = event.currentTarget as HTMLElement | null
    setAnchorEl((prev) => (prev ? null : target))
  }

  const handleClose = (ev: any) => {
    // MUI calls onClose with a plain {} (no stopPropagation) for keyboard events like Escape
    if (ev && typeof ev.stopPropagation === 'function') {
      ev.stopPropagation()
    }
    setAnchorEl(null)
  }

  const popupButton = (component: any) => {
    if (!component) return null
    else
      return React.cloneElement(props.component, {
        'aria-haspopup': true,
        'aria-owns': anchorEl ? props.id : null,
        onClick: (ev: React.SyntheticEvent) => {
          if (!props.disabled && !props.component.props.disabled) {
            handleToggle(ev)
          }

          if (props.component.props.onClick) {
            props.component.props.onClick(ev)
          }
        }
      })
  }

  // Destructure out props that are specific to PopupMenu and shouldn't be forwarded to MUI Menu
  const { component, disabled, orientation, className, children, id, getContentAnchorEl, ...menuProps } = props

  return (
    <div className={classnames(classes.root, className)}>
      {popupButton(component)}
      <Menu
        onClick={(ev) => {
          handleToggle(ev)
          ev.stopPropagation()
        }}
        id={id}
        anchorEl={anchorEl}
        getContentAnchorEl={getContentAnchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        {...menuProps}
        classes={{ paper: classes.menu, list: classes.list }}
      >
        {children}
      </Menu>
    </div>
  )
}

export default PopupMenu
